import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Trash2, Gauge, Timer, Weight, CheckCircle2, AlertCircle,
  Info, ChevronRight, Save, RotateCcw, Microscope,
  TrendingUp, BarChart3, Zap, Thermometer, CircleDot,
  Factory, Milk, FishSymbol, ShieldAlert, Droplets,
  ArrowDown, ArrowRightLeft, Layers,
  Settings, FlaskConical, Filter, Database, Beaker
} from 'lucide-react';
import { useSampleLoader, fmt as sharedFmt } from '@/lib/equipmentSpecUtils';
import { SampleSelector } from '@/components/SampleSelector';
import PageHeader from '@/components/PageHeader';
import { ScienceAccordion } from '@/components/ScienceAccordion';

/* ─── Sludge Holding / Thickening Tank Specification ─────
   Gravity thickening with polymer dosing, solids balance,
   storage capacity, and dewatering readiness
   ──────────────────────────────────────────────────── */

interface SludgeParams {
  flowRate: number;         // m³/day of sludge incoming
  inletDS: number;          // % dry solids in
  targetDS: number;         // % dry solids out (thickened)
  // Tank
  tankDiameter: number;     // m
  tankDepth: number;        // m operating
  freeboard: number;        // m
  workingVolumePercent: number; // %
  // Polymer
  polymerDose: number;      // kg polymer / tonne DS
  polymerConcentration: number; // % w/w
  // Process
  thickeningTime: number;   // h
  temperature: number;      // °C
  // Dewatering
  dewateringType: 'belt_press' | 'centrifuge' | 'filter_press' | 'none';
  og: number; ph: number; alkalinity: number; turbidity: number;
  conductivity: number; hardness: number; nh3n: number; tp: number;
}

interface SludgeSize {
  tankVolume: number;       // m³ total
  workingVolume: number;    // m³
  tankArea: number;         // m²
  // Solids
  inletTonneDS: number;     // tonne/day DS in
  outletTonneDS: number;    // tonne/day DS out (same mass)
  thickenedVolume: number;  // m³/day out
  supernatantVolume: number; // m³/day returned
  thickeningFactor: number; // ratio
  // Storage
  storageDays: number;      // days at working volume
  detentionTime: number;    // h average
  peakDetention: number;    // h at 1.5× peak
  // Polymer
  polymerDaily: number;     // kg/day polymer required
  polymerVolume: number;    // L/day of polymer solution
  polymerPumpRate: number;  // mL/min
  // Status
  status: 'optimal' | 'acceptable' | 'undersized' | 'oversized' | 'overloaded';
  statusDetail: string;
  // Risk
  overfillRisk: 'low' | 'moderate' | 'high';
  anaerobicRisk: 'low' | 'moderate' | 'high';
  polymerRisk: 'low' | 'moderate' | 'high';
  // Extras for diagram
  totalVolume: number;
  actualDetention: number;
  volumeTurnover: number;
  depthIncrease: number;
  overflowRisk: number;
  thickeningRate: number;
  freeboardVolume: number;
  power: number;
}

const fmt = (v: any, d = 1) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

const PROFILES = [
  { name: 'Dairy Effluent', description: 'Primary + secondary sludge. 3% DS in, target 6%.', icon: Milk,
    defaults: { flowRate: 25, inletDS: 3.0, targetDS: 6.0,
      og: 120, ph: 6.8, alkalinity: 180, turbidity: 380, conductivity: 2500, hardness: 250, nh3n: 45, tp: 12,
      tankDiameter: 5, tankDepth: 3.5, freeboard: 0.5, workingVolumePercent: 70,
      polymerDose: 4.0, polymerConcentration: 0.25,
      thickeningTime: 18, temperature: 25, dewateringType: 'belt_press' } },
  { name: 'Brewery Effluent', description: 'High organic sludge. 2.5% DS in, target 5%.', icon: Factory,
    defaults: { flowRate: 35, inletDS: 2.5, targetDS: 5.0,
      og: 180, ph: 5.2, alkalinity: 80, turbidity: 550, conductivity: 4200, hardness: 350, nh3n: 85, tp: 18,
      tankDiameter: 6, tankDepth: 4.0, freeboard: 0.5, workingVolumePercent: 65,
      polymerDose: 5.5, polymerConcentration: 0.25,
      thickeningTime: 24, temperature: 20, dewateringType: 'centrifuge' } },
  { name: 'Fish Processing', description: 'Oily, fibrous sludge. 2% DS in, target 4%.', icon: FishSymbol,
    defaults: { flowRate: 15, inletDS: 2.0, targetDS: 4.0,
      og: 220, ph: 6.9, alkalinity: 220, turbidity: 480, conductivity: 1800, hardness: 180, nh3n: 35, tp: 8,
      tankDiameter: 4, tankDepth: 3.0, freeboard: 0.5, workingVolumePercent: 60,
      polymerDose: 6.0, polymerConcentration: 0.2,
      thickeningTime: 20, temperature: 15, dewateringType: 'filter_press' } },
];

export function calcSludge(p: SludgeParams): SludgeSize {
  const q = p.flowRate / 24; // m³/h

  // ─── Water properties ───
  const tempK = p.temperature + 273.15;
  const mu = 0.001 * Math.pow(10, 247.8 / tempK - 140); // Pa·s
  const rhoWater = 998 - 0.2 * (p.temperature - 20); // kg/m³

  // ─── Tank geometry ───
  const area = Math.PI * Math.pow(p.tankDiameter / 2, 2);
  const totalVolume = area * (p.tankDepth + p.freeboard);
  const workingVolume = totalVolume * (p.workingVolumePercent / 100);
  const freeboardVolume = totalVolume - workingVolume;

  // ─── Solids mass balance ───
  const inletDSFraction = p.inletDS / 100; // decimal
  const targetDSFraction = p.targetDS / 100; // decimal
  const inletMassFlow = p.flowRate * inletDSFraction * 1000; // kg/day (assume sludge density ~1000)
  const drySolidsIn = inletMassFlow; // kg/day DS

  // ─── Thickening physics ───
  // Batch flux curve: settling flux = C × v(C) where v = settling velocity at concentration C
  // Vesilind-type: v = v0 × exp(-k × C)
  const v0 = 5; // m/h (initial settling velocity for thin sludge)
  const kThickening = 0.3; // L/g (empirical for WAS)
  const inletConcGPerL = inletDSFraction * 1000 * 10; // g/L TSS (approx)
  const settlingVelocity = v0 * Math.exp(-kThickening * inletConcGPerL / 1000); // m/h

  // ─── Gravity thickening area requirement ───
  // Flux = C × v.  For continuous thickener: area = Q_in × C_in / (C_lim × v_lim)
  const limitingFlux = inletConcGPerL * settlingVelocity; // g/m²·h
  const solidsFlux = (drySolidsIn * 1000) / 24; // g/h
  const requiredArea = limitingFlux > 0 ? solidsFlux / limitingFlux : 0; // m²
  const actualFlux = drySolidsIn > 0 ? (drySolidsIn / 24) / area : 0; // kg/m²·h

  // ─── Thickening rate and time ───
  const thickeningRate = ((targetDSFraction - inletDSFraction) / inletDSFraction) * 100; // % concentration increase
  const thickeningTimeCalc = thickeningRate > 0 ? (workingVolume / q) * (thickeningRate / 100) : 0; // h

  // ─── Volume reduction ───
  // Mass of solids is conserved: Q_in × C_in = Q_out × C_out
  const outletFlow = inletDSFraction > 0 ? (p.flowRate * inletDSFraction) / targetDSFraction : 0; // m³/day (thickener underflow)
  const volumeReduction = p.flowRate > 0 ? ((p.flowRate - outletFlow) / p.flowRate) * 100 : 0; // %
  const supernatantFlow = p.flowRate - outletFlow; // m³/day (overflow/decant)

  // ─── Polymer dosing ───
  const polymerDose = p.polymerDose; // kg polymer / ton DS
  const polymerConsumption = (drySolidsIn / 1000) * polymerDose; // kg/day
  // Polymer improves settling: increases effective flux by bridging particles
  const polymerBoost = polymerDose > 0 ? 1 + (polymerDose / 5) * 0.3 : 1.0; // up to 30% boost at 5 kg/t
  const enhancedSettlingVelocity = settlingVelocity * polymerBoost;

  // ─── Storage capacity ───
  const storageDays = outletFlow > 0 ? workingVolume / outletFlow : 0; // days at thickened rate
  const detentionTime = workingVolume / q; // h average
  const peakDetention = workingVolume / (q * 1.5); // h at 1.5× peak

  // ─── Decant quality ───
  const supernatantTSS = p.inletDS > 0 ? Math.max(100, p.inletDS * 50) : 0; // mg/L (some solids escape)

  // ─── Depth increase ───
  const depthIncrease = outletFlow > 0 ? (outletFlow / area) * 1000 : 0; // mm/day

  // ─── Volume turnover ───
  const volumeTurnover = workingVolume > 0 ? p.flowRate / workingVolume : 0; // times/day

  // ─── Overflow risk ───
  const overflowRisk = totalVolume > 0 ? (1 - (workingVolume / totalVolume)) * 100 : 0;

  // ─── Power (scrapers/pumps simplified) ───
  const power = area * 0.05; // kW (rough estimate for scraper)

  // ─── Status ───
  let status: SludgeSize['status']; let statusDetail: string;
  if (thickeningRate >= 50 && storageDays >= 1 && volumeReduction >= 40 && actualFlux <= 20) {
    status = 'optimal'; statusDetail = `Optimal. Thickening ${thickeningRate.toFixed(0)}%, storage ${storageDays.toFixed(1)} d, flux ${actualFlux.toFixed(1)} kg/m²·h.`;
  } else if (thickeningRate >= 30 && storageDays >= 0.5 && volumeReduction >= 25) {
    status = 'acceptable'; statusDetail = `Acceptable. Thickening ${thickeningRate.toFixed(0)}%. Monitor storage.`;
  } else if (actualFlux > 50 || thickeningRate < 10 || storageDays < 0.2) {
    status = 'overloaded'; statusDetail = `Overloaded. Flux ${actualFlux.toFixed(1)} kg/m²·h or thickening ${thickeningRate.toFixed(0)}% too low. Increase area or polymer dose.`;
  } else if (workingVolume < 2 || totalVolume < 5) {
    status = 'undersized'; statusDetail = `Undersized. Working volume ${workingVolume.toFixed(1)} m³. Increase tank size.`;
  } else {
    status = 'oversized'; statusDetail = `Oversized. Consider reducing for cost savings.`;
  }

  return {
    tankVolume: totalVolume, workingVolume, tankArea: area,
    inletTonneDS: drySolidsIn / 1000,
    outletTonneDS: drySolidsIn / 1000, // mass conserved
    thickenedVolume: outletFlow,
    supernatantVolume: supernatantFlow,
    thickeningFactor: targetDSFraction / Math.max(inletDSFraction, 0.001),
    polymerDaily: polymerConsumption,
    polymerVolume: polymerConsumption / Math.max(p.polymerConcentration / 100, 0.001),
    polymerPumpRate: (polymerConsumption / Math.max(p.polymerConcentration / 100, 0.001) * 1000) / (24 * 60),
    overfillRisk: storageDays < 0.5 ? 'high' : storageDays < 1 ? 'moderate' : 'low',
    anaerobicRisk: detentionTime > 24 ? 'high' : detentionTime > 12 ? 'moderate' : 'low',
    polymerRisk: polymerConsumption > 50 ? 'high' : polymerConsumption > 20 ? 'moderate' : 'low',
    storageDays, detentionTime, peakDetention,
    thickeningRate, thickeningTime: thickeningTimeCalc,
    volumeReduction, actualFlux, settlingVelocity, enhancedSettlingVelocity,
    status, statusDetail,
    // extras
    totalVolume, actualDetention: detentionTime, volumeTurnover, depthIncrease, overflowRisk, freeboardVolume, power,
  };
}

/* ─── UI Components ──────────────────────────────────── */
function S({ label, value, min, max, step, unit, icon: Icon, color = '#06b6d4', onChange, info }: any) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <span className="text-xs font-medium text-slate-300">{label}</span>
          {info && <div className="group relative"><Info className="w-3 h-3 text-slate-600 cursor-help" /><div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 rb-card-sm">{info}</div></div>}
        </div>
        <span className="text-xs font-bold text-white tabular-nums">{step < 0.1 ? value.toFixed(2) : step < 1 ? value.toFixed(1) : value.toFixed(0)}<span className="text-[10px] text-slate-500 font-normal ml-0.5">{unit}</span></span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer" style={{ accentColor: color }} />
    </div>
  );
}

function G({ value, max, label, unit, color }: any) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]"><span className="text-slate-500">{label}</span><span className="font-bold" style={{ color }}>{typeof value === 'number' ? value.toFixed(1) : value} {unit}</span></div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} className="h-full rounded-full" style={{ backgroundColor: color }} /></div>
    </div>
  );
}

function PC({ profile, isActive, onClick }: any) {
  const Icon = profile.icon;
  return (
    <button onClick={onClick} className={`w-full text-left p-3 rounded-xl border transition-all ${isActive ? 'bg-amber-500/10 border-amber-500/30' : 'bg-rb-elevated border-slate-800 hover:border-slate-700'}`}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-amber-500/20' : 'bg-slate-800'}`}><Icon className={`w-4 h-4 ${isActive ? 'text-amber-400' : 'text-slate-500'}`} /></div>
        <div><p className={`text-xs font-semibold ${isActive ? 'text-amber-400' : 'text-slate-300'}`}>{profile.name}</p><p className="text-[10px] text-slate-500 leading-tight">{profile.description}</p></div>
      </div>
    </button>
  );
}

function AB({ level, title, message }: any) {
  const s: any = { low: { b: 'border-emerald-500/20', bg: 'bg-emerald-500/5', c: 'text-emerald-400', i: CheckCircle2 }, moderate: { b: 'border-amber-500/20', bg: 'bg-amber-500/5', c: 'text-amber-400', i: AlertCircle }, high: { b: 'border-orange-500/20', bg: 'bg-orange-500/5', c: 'text-orange-400', i: ShieldAlert }, critical: { b: 'border-rose-500/20', bg: 'bg-rose-500/5', c: 'text-rose-400', i: ShieldAlert } };
  const Icon = s[level].i;
  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className={`rounded-xl border p-3 ${s[level].bg} ${s[level].b}`}>
      <div className="flex items-center gap-2"><Icon className={`w-4 h-4 ${s[level].c}`} /><span className={`text-xs font-bold uppercase ${s[level].c}`}>{title}</span></div>
      <p className={`text-[10px] mt-1 leading-relaxed ${s[level].c} opacity-80`}>{message}</p>
    </motion.div>
  );
}

/* ─── Tank Visual ───────────────────────────────────── */
function TankVisual({ diameter, depth, freeboard, dsPercent }: any) {
  const totalDepth = depth + freeboard;
  const sludgeLevel = (depth / totalDepth) * 100;
  return (
    <div className="p-4 flex items-center justify-center rb-card-sm">
      <svg viewBox="0 0 140 160" className="w-full h-36">
        <ellipse cx="70" cy="10" rx="60" ry="12" fill="none" stroke="var(--rb-active)" strokeWidth="2" />
        <ellipse cx="70" cy={10 + sludgeLevel * 1.3} rx="60" ry="12" fill="var(--rb-active)" opacity="0.4" stroke="var(--rb-active)" strokeWidth="1" />
        <line x1="10" y1="10" x2="10" y2={10 + sludgeLevel * 1.3} stroke="var(--rb-active)" strokeWidth="2" />
        <line x1="130" y1="10" x2="130" y2={10 + sludgeLevel * 1.3} stroke="var(--rb-active)" strokeWidth="2" />
        <rect x="10" y={10 + sludgeLevel * 0.3} width="120" height={sludgeLevel} fill="#8B4513" opacity="0.6" />
        <rect x="10" y="10" width="120" height={sludgeLevel * 0.3} fill="#38bdf8" opacity="0.2" />
        <text x="70" y={10 + sludgeLevel * 0.8} fill="#d97706" fontSize="10" textAnchor="middle" fontWeight="bold">{dsPercent}% DS</text>
        <text x="70" y="155" fill="#64748b" fontSize="10" textAnchor="middle">{diameter}m × {totalDepth.toFixed(1)}m</text>
      </svg>
    </div>
  );
}

/* ─── Enterprise Animated Flow Diagram ────────────────── */
function FlowDiagram({ params, size }: { params: SludgeParams; size: SludgeSize }) {
  const safe = {
    totalVolume: typeof size?.totalVolume === 'number' ? size.totalVolume : 0,
    workingVolume: typeof size?.workingVolume === 'number' ? size.workingVolume : 0,
    tankVolume: typeof size?.tankVolume === 'number' ? size.tankVolume : 0,
    actualDetention: typeof size?.actualDetention === 'number' ? size.actualDetention : 0,
    peakDetention: typeof size?.peakDetention === 'number' ? size.peakDetention : 0,
    freeboardVolume: typeof size?.freeboardVolume === 'number' ? size.freeboardVolume : 0,
    thickeningRate: typeof size?.thickeningRate === 'number' ? size.thickeningRate : 0,
    volumeTurnover: typeof size?.volumeTurnover === 'number' ? size.volumeTurnover : 0,
    depthIncrease: typeof size?.depthIncrease === 'number' ? size.depthIncrease : 0,
    overflowRisk: typeof size?.overflowRisk === 'number' ? size.overflowRisk : 0,
    power: typeof size?.power === 'number' ? size.power : 0,
  };

  return (
    <div className="p-4 rb-card-sm">
      <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-wider font-bold flex items-center gap-2">
        <ArrowDown className="w-3 h-3" /> Process Flow — Sludge Influent → Holding Tank → Thickened Sludge → Decant
      </p>
      <svg viewBox="0 0 900 380" className="w-full h-auto">
        <defs>
          <linearGradient id="waterGradSL" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--rb-active)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#0f2847" stopOpacity="0.8" />
          </linearGradient>
          <linearGradient id="sludgeGradSL" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8B4513" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#5c2e0b" stopOpacity="0.7" />
          </linearGradient>
        </defs>

        <text x="450" y="18" fill="#94a3b8" fontSize="11" textAnchor="middle" fontWeight="bold">SLUDGE HOLDING TANK — THICKENING &amp; STORAGE TRAIN</text>

        {/* Sludge Influent */}
        <rect x="30" y="55" width="100" height="80" rx="6" fill="url(#sludgeGradSL)" stroke="#8B4513" strokeWidth="2" />
        <rect x="34" y="59" width="92" height="20" rx="3" fill="#f59e0b" opacity="0.15" />
        <text x="80" y="72" fill="#f59e0b" fontSize="9" textAnchor="middle" fontWeight="bold">Sludge Influent</text>
        <text x="80" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">{fmt(params.flowRate, 0)} m³/day</text>
        <text x="80" y="100" fill="#f59e0b" fontSize="8" textAnchor="middle">DS {fmt(params.inletDS, 1)}% | {fmt(params.inletDS * 10000, 0)} mg/L</text>
        <text x="80" y="112" fill="#94a3b8" fontSize="8" textAnchor="middle">Temp {fmt(params.temperature, 0)}°C</text>
        <text x="80" y="124" fill="#94a3b8" fontSize="8" textAnchor="middle">pH —</text>

        <line x1="132" y1="95" x2="170" y2="95" stroke="#8B4513" strokeWidth="2.5" strokeDasharray="5 3" />
        <polygon points="168,92 175,95 168,98" fill="#8B4513" />

        {/* Sludge Pump */}
        <g transform="translate(172, 78)">
          <circle cx="18" cy="14" r="14" fill="#0f172a" stroke="#8B4513" strokeWidth="1.5" />
          <line x1="18" y1="6" x2="18" y2="22" stroke="#8B4513" strokeWidth="1.5" />
          <line x1="10" y1="14" x2="26" y2="14" stroke="#8B4513" strokeWidth="1.5" />
          <rect x="-6" y="10" width="12" height="8" rx="2" fill="var(--rb-active)" stroke="#8B4513" strokeWidth="1" />
          <text x="18" y="38" fill="#8B4513" fontSize="7" textAnchor="middle">Sludge Pump</text>
        </g>

        <line x1="212" y1="95" x2="250" y2="95" stroke="#8B4513" strokeWidth="2.5" strokeDasharray="5 3" />
        <polygon points="248,92 255,95 248,98" fill="#8B4513" />

        {/* Holding Tank */}
        <rect x="255" y="35" width="180" height="120" rx="6" fill="url(#sludgeGradSL)" stroke="#f59e0b" strokeWidth="2" />
        <text x="345" y="52" fill="#f59e0b" fontSize="9" textAnchor="middle" fontWeight="bold">Holding Tank</text>
        <text x="345" y="68" fill="#94a3b8" fontSize="8" textAnchor="middle">{fmt(params.tankDiameter, 1)} m Ø × {fmt(params.tankDepth, 1)} m deep</text>
        <text x="345" y="82" fill="#94a3b8" fontSize="8" textAnchor="middle">Total {fmt(safe.totalVolume, 1)} m³ | Working {fmt(safe.workingVolume, 1)} m³</text>
        <text x="345" y="96" fill="#f59e0b" fontSize="8" textAnchor="middle">Detention {fmt(safe.actualDetention, 1)} h avg | Peak {fmt(safe.peakDetention, 1)} h</text>
        <text x="345" y="110" fill="#f59e0b" fontSize="8" textAnchor="middle">Thickening {fmt(safe.thickeningRate, 1)}% DS | Turnover {fmt(safe.volumeTurnover, 1)}×/d</text>
        <text x="345" y="124" fill="#94a3b8" fontSize="8" textAnchor="middle">Depth increase {fmt(safe.depthIncrease, 1)} mm/day</text>
        <text x="345" y="138" fill="#94a3b8" fontSize="8" textAnchor="middle">Overflow risk {fmt(safe.overflowRisk, 0)}%</text>

        {/* Decant line */}
        <line x1="345" y1="158" x2="345" y2="220" stroke="#38bdf8" strokeWidth="1.5" strokeDasharray="4 3" />
        <line x1="345" y1="220" x2="250" y2="220" stroke="#38bdf8" strokeWidth="1.5" strokeDasharray="4 3" />
        <polygon points="252,217 245,220 252,223" fill="#38bdf8" />

        {/* Decant tank */}
        <rect x="130" y="200" width="110" height="40" rx="4" fill="#0f172a" stroke="#38bdf8" strokeWidth="1.5" />
        <text x="185" y="218" fill="#38bdf8" fontSize="8" textAnchor="middle">Decant</text>
        <text x="185" y="230" fill="#94a3b8" fontSize="7" textAnchor="middle">Supernatant return</text>

        {/* Thickened sludge line */}
        <line x1="437" y1="95" x2="480" y2="95" stroke="#8B4513" strokeWidth="2.5" strokeDasharray="5 3" />
        <polygon points="478,92 485,95 478,98" fill="#8B4513" />

        {/* Thickened Sludge */}
        <rect x="487" y="55" width="100" height="80" rx="6" fill="url(#sludgeGradSL)" stroke="#f59e0b" strokeWidth="2" />
        <rect x="491" y="59" width="92" height="20" rx="3" fill="#f59e0b" opacity="0.15" />
        <text x="537" y="72" fill="#f59e0b" fontSize="9" textAnchor="middle" fontWeight="bold">Thickened Sludge</text>
        <text x="537" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">{fmt(params.targetDS, 1)}% DS target</text>
        <text x="537" y="100" fill="#94a3b8" fontSize="8" textAnchor="middle">Detention {fmt(safe.actualDetention, 1)} h</text>
        <text x="537" y="112" fill="#f59e0b" fontSize="8" textAnchor="middle">Thickening {fmt(safe.thickeningRate, 1)}% DS</text>
        <text x="537" y="124" fill="#94a3b8" fontSize="8" textAnchor="middle">Depth +{fmt(safe.depthIncrease, 1)} mm/d</text>

        {/* Legend */}
        <rect x="30" y="345" width="840" height="24" rx="4" fill="#0f172a" stroke="var(--rb-active)" strokeWidth="1" />
        <text x="45" y="359" fill="#8B4513" fontSize="9">— Sludge Flow</text>
        <text x="145" y="359" fill="#8B4513" fontSize="9">— Sludge Pump</text>
        <text x="245" y="359" fill="#f59e0b" fontSize="9">— Holding Tank</text>
        <text x="345" y="359" fill="#38bdf8" fontSize="9">— Decant</text>
        <text x="445" y="359" fill="#94a3b8" fontSize="9">τ = V/Q | θ = (DSout−DSin)/DSin | V = Q×(DSout−DSin)/ρsludge</text>
      </svg>
    </div>
  );
}

export default function SludgeHoldingSpecPage() {
  const [params, setParams] = useState<SludgeParams>({
    flowRate: 25, inletDS: 3.0, targetDS: 6.0,
    og: 120, ph: 6.8, alkalinity: 180, turbidity: 380, conductivity: 2500, hardness: 250, nh3n: 45, tp: 12,
    tankDiameter: 5, tankDepth: 3.5, freeboard: 0.5, workingVolumePercent: 70,
    polymerDose: 4.0, polymerConcentration: 0.25,
    thickeningTime: 18, temperature: 25, dewateringType: 'belt_press',
  });

  const [activeProfile, setActiveProfile] = useState(0);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();
  const [showFlow, setShowFlow] = useState(true);
  const [activeTab, setActiveTab] = useState<'thickening' | 'polymer'>('thickening');
  const size = useMemo(() => calcSludge(params), [params]);
  const sampleLoader = useSampleLoader<SludgeParams>(setParams);

  const update = (key: keyof SludgeParams, val: number | string) => { setParams(p => ({ ...p, [key]: val })); setSaved(false); };
  const loadProfile = (idx: number) => { const prof = PROFILES[idx]; if (prof.defaults) { setParams(p => ({ ...p, ...(prof.defaults as Partial<SludgeParams>) })); setActiveProfile(idx); setSaved(false); toast.success(`Loaded ${prof.name} profile`); } };
  const handleSave = () => { localStorage.setItem('rb_sludge_spec', JSON.stringify(params)); setSaved(true); toast.success('Sludge specification saved'); };
  const handleReset = () => { loadProfile(0); setSaved(false); sampleLoader.clearSample(); };

  useEffect(() => { const s = localStorage.getItem('rb_sludge_spec'); if (s) { try { setParams(JSON.parse(s)); } catch {} } }, []);

  // Science accordion sections
  const scienceSections = useMemo(() => {
    const q = params.flowRate / 24;
    const area = Math.PI * Math.pow(params.tankDiameter / 2, 2);
    const totalVolume = area * (params.tankDepth + params.freeboard);
    const workingVolume = totalVolume * (params.workingVolumePercent / 100);
    const inletDSFraction = params.inletDS / 100;
    const targetDSFraction = params.targetDS / 100;
    const drySolidsIn = params.flowRate * inletDSFraction * 1000;
    const v0 = 5;
    const kThickening = 0.3;
    const inletConcGPerL = inletDSFraction * 1000 * 10;
    const settlingVelocity = v0 * Math.exp(-kThickening * inletConcGPerL / 1000);
    const limitingFlux = inletConcGPerL * settlingVelocity;
    const solidsFlux = (drySolidsIn * 1000) / 24;
    const requiredArea = limitingFlux > 0 ? solidsFlux / limitingFlux : 0;
    const actualFlux = drySolidsIn > 0 ? (drySolidsIn / 24) / area : 0;
    const thickeningRate = ((targetDSFraction - inletDSFraction) / inletDSFraction) * 100;
    const outletFlow = inletDSFraction > 0 ? (params.flowRate * inletDSFraction) / targetDSFraction : 0;
    const volumeReduction = params.flowRate > 0 ? ((params.flowRate - outletFlow) / params.flowRate) * 100 : 0;
    const supernatantFlow = params.flowRate - outletFlow;
    const polymerConsumption = (drySolidsIn / 1000) * params.polymerDose;
    const polymerBoost = params.polymerDose > 0 ? 1 + (params.polymerDose / 5) * 0.3 : 1.0;
    const enhancedSettlingVelocity = settlingVelocity * polymerBoost;
    const storageDays = outletFlow > 0 ? workingVolume / outletFlow : 0;
    const detentionTime = workingVolume / q;
    return [
      {
        title: 'Vesilind Settling',
        icon: Droplets,
        equations: [
          { label: 'Inlet concentration', formula: 'C = DS% × 1000 × 10', substitution: `C = ${params.inletDS}% × 1000 × 10 = ${sharedFmt(inletConcGPerL, 0)} g/L`, result: sharedFmt(inletConcGPerL, 0), status: 'pass', unit: 'g/L' },
          { label: 'Settling velocity', formula: 'v = v₀ × exp(-k × C/1000)', substitution: `v = 5 × exp(-0.3 × ${sharedFmt(inletConcGPerL, 0)}/1000) = ${sharedFmt(settlingVelocity, 2)} m/h`, result: sharedFmt(settlingVelocity, 2), status: settlingVelocity > 1 ? 'pass' : 'warn', unit: 'm/h' },
          { label: 'Limiting flux', formula: 'F = C × v', substitution: `F = ${sharedFmt(inletConcGPerL, 0)} × ${sharedFmt(settlingVelocity, 2)} = ${sharedFmt(limitingFlux, 0)} g/m²·h`, result: sharedFmt(limitingFlux, 0), status: 'pass', unit: 'g/m²·h' },
          { label: 'Required area', formula: 'A = solidsFlux / limitingFlux', substitution: `A = ${sharedFmt(solidsFlux, 0)} / ${sharedFmt(limitingFlux, 0)} = ${sharedFmt(requiredArea, 1)} m²`, result: sharedFmt(requiredArea, 1), status: requiredArea <= area ? 'pass' : 'warn', unit: 'm²' },
        ],
      },
      {
        title: 'Solids Flux & Thickening',
        icon: Layers,
        equations: [
          { label: 'Tank area', formula: 'A = π × (D/2)²', substitution: `A = π × (${params.tankDiameter}/2)² = ${sharedFmt(area, 1)} m²`, result: sharedFmt(area, 1), status: 'pass', unit: 'm²' },
          { label: 'Actual flux', formula: 'F = (DS_in / 24) / A', substitution: `F = (${sharedFmt(drySolidsIn, 0)}/24) / ${sharedFmt(area, 1)} = ${sharedFmt(actualFlux, 1)} kg/m²·h`, result: sharedFmt(actualFlux, 1), status: actualFlux <= 20 ? 'pass' : actualFlux <= 50 ? 'warn' : 'fail', unit: 'kg/m²·h' },
          { label: 'Thickening rate', formula: 'θ = (DS_out - DS_in) / DS_in × 100', substitution: `θ = (${params.targetDS} - ${params.inletDS}) / ${params.inletDS} × 100 = ${sharedFmt(thickeningRate, 0)}%`, result: sharedFmt(thickeningRate, 0), status: thickeningRate >= 30 ? 'pass' : 'warn', unit: '%' },
          { label: 'Volume reduction', formula: 'R = (Q_in - Q_out) / Q_in × 100', substitution: `R = (${sharedFmt(params.flowRate, 0)} - ${sharedFmt(outletFlow, 1)}) / ${sharedFmt(params.flowRate, 0)} × 100 = ${sharedFmt(volumeReduction, 0)}%`, result: sharedFmt(volumeReduction, 0), status: volumeReduction >= 25 ? 'pass' : 'warn', unit: '%' },
          { label: 'Supernatant flow', formula: 'Q_s = Q_in - Q_out', substitution: `Q_s = ${sharedFmt(params.flowRate, 0)} - ${sharedFmt(outletFlow, 1)} = ${sharedFmt(supernatantFlow, 1)} m³/d`, result: sharedFmt(supernatantFlow, 1), status: 'pass', unit: 'm³/d' },
        ],
      },
      {
        title: 'Polymer Dosing & Storage',
        icon: FlaskConical,
        equations: [
          { label: 'Polymer consumption', formula: 'm_p = (DS_tonnes/d) × dose', substitution: `m_p = (${sharedFmt(drySolidsIn / 1000, 2)}) × ${params.polymerDose} = ${sharedFmt(polymerConsumption, 1)} kg/d`, result: sharedFmt(polymerConsumption, 1), status: polymerConsumption <= 50 ? 'pass' : 'warn', unit: 'kg/d' },
          { label: 'Polymer boost', formula: 'Boost = 1 + (dose/5) × 0.3', substitution: `Boost = 1 + (${params.polymerDose}/5) × 0.3 = ${sharedFmt(polymerBoost, 2)}×`, result: sharedFmt(polymerBoost, 2), status: 'pass', unit: '' },
          { label: 'Enhanced settling', formula: 'v_enh = v × boost', substitution: `v_enh = ${sharedFmt(settlingVelocity, 2)} × ${sharedFmt(polymerBoost, 2)} = ${sharedFmt(enhancedSettlingVelocity, 2)} m/h`, result: sharedFmt(enhancedSettlingVelocity, 2), status: 'pass', unit: 'm/h' },
          { label: 'Storage days', formula: 'S = V_working / Q_out', substitution: `S = ${sharedFmt(workingVolume, 1)} / ${sharedFmt(outletFlow, 1)} = ${sharedFmt(storageDays, 1)} d`, result: sharedFmt(storageDays, 1), status: storageDays >= 1 ? 'pass' : storageDays >= 0.5 ? 'warn' : 'fail', unit: 'days' },
          { label: 'Detention time', formula: 'τ = V_working / Q', substitution: `τ = ${sharedFmt(workingVolume, 1)} / ${sharedFmt(q, 2)} = ${sharedFmt(detentionTime, 1)} h`, result: sharedFmt(detentionTime, 1), status: detentionTime <= 24 ? 'pass' : 'warn', unit: 'h' },
        ],
      },
    ];
  }, [params]);

  const statusColors: any = {
    optimal: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle2 },
    acceptable: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', icon: Info },
    undersized: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20', icon: AlertCircle },
    overloaded: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: AlertCircle },
    oversized: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Info },
  };
  const sc = statusColors[size.status];
  const StatusIcon = sc.icon;

  return (
    <div className="max-w-[1440px] mx-auto space-y-5">
      <PageHeader
        icon={Droplets}
        iconColor="text-amber-400"
        title="Sludge Holding / Thickening Tank Specification"
        subtitle="Vesilind settling, solids flux, polymer dosing, mass balance"
        actions={<div className="flex items-center gap-2">
          <button onClick={handleReset} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-slate-800/80 border border-slate-800/60 text-slate-300 text-xs font-bold hover:bg-slate-700 hover:border-slate-600 transition-all"><RotateCcw className="w-3.5 h-3.5" />Reset</button>
          <button onClick={handleSave} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-amber-500/10 text-amber-400 text-xs font-bold border border-amber-500/20 hover:bg-amber-500/20 transition-all"><Save className="w-3.5 h-3.5" />{saved ? '✓ Saved' : 'Save'}</button>
          <button onClick={() => navigate('/equipment-validation?tab=sludge')} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-sky-500/10 text-sky-400 text-xs font-bold border border-sky-500/20 hover:bg-sky-500/20 transition-all"><Microscope className="w-3.5 h-3.5" />Validation</button>
        </div>}
      />

      {/* Sample Selector */}
      <SampleSelector loader={sampleLoader} />

      {/* Loaded sample readout */}
      {sampleLoader.baseSampleParams && (
        <div className="bg-rb-surface rounded-xl border border-sky-500/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Database className="w-4 h-4 text-sky-400" />Loaded Sample: Base → Current</h3>
            <span className="text-xs text-slate-500">Dilution: {sampleLoader.dilutionFactor.toFixed(1)}×</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-2 text-xs">
            {[
              { label: 'Flow', value: `${sharedFmt(params.flowRate, 0)} m³/d`, base: `${sharedFmt(sampleLoader.baseSampleParams.flowRate || 0, 0)}` },
              { label: 'O&G', value: `${sharedFmt(params.og, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.og || 0, 0)}` },
              { label: 'pH', value: `${sharedFmt(params.ph, 1)}`, base: `${sharedFmt(sampleLoader.baseSampleParams.ph || 0, 1)}` },
              { label: 'Temp', value: `${sharedFmt(params.temperature, 0)}°C`, base: `${sharedFmt(sampleLoader.baseSampleParams.temp || 0, 0)}°C` },
              { label: 'Alk', value: `${sharedFmt(params.alkalinity, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.alkalinity || 0, 0)}` },
              { label: 'Turb', value: `${sharedFmt(params.turbidity, 0)} NTU`, base: `${sharedFmt(sampleLoader.baseSampleParams.turbidity || 0, 0)}` },
              { label: 'Cond', value: `${sharedFmt(params.conductivity, 0)} μS/cm`, base: `${sharedFmt(sampleLoader.baseSampleParams.conductivity || 0, 0)}` },
              { label: 'Hard', value: `${sharedFmt(params.hardness, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.hardness || 0, 0)}` },
              { label: 'NH₃-N', value: `${sharedFmt(params.nh3n, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.nh3n || 0, 0)}` },
              { label: 'TP', value: `${sharedFmt(params.tp, 1)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tp || 0, 1)}` },
            ].map((item) => (
              <div key={item.label} className="p-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase">{item.label}</p>
                <p className="text-sm font-bold text-white">{item.value}</p>
                <p className="text-[10px] text-slate-600">Base: {item.base}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        {/* LEFT: Flow Panel */}
        <div className="xl:col-span-7 space-y-4">
          <div className="flex items-center justify-between px-4 py-2 rb-inner-card">
            <h3 className="text-xs font-semibold text-cyan-400 flex items-center gap-2">
              <ArrowRightLeft className="w-3.5 h-3.5" /> Process Flow Diagram
            </h3>
            <button onClick={() => setShowFlow(!showFlow)} className="text-[10px] px-2 py-1 rounded-md bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20 transition-all">
              {showFlow ? 'Collapse' : 'Expand'}
            </button>
          </div>
          <div className="mb-2 text-center">
            <p className="text-[10px] font-bold tracking-widest text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] uppercase">
              Reynolds &amp; Bauhm Smart Tool 2.0 — All Rights Reserved
            </p>
          </div>
          {showFlow && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }}>
              <FlowDiagram params={params} size={size} />
            </motion.div>
          )}
          <TankVisual diameter={params.tankDiameter} depth={params.tankDepth} freeboard={params.freeboard} dsPercent={params.inletDS} />
        </div>

        {/* RIGHT: Content Panel */}
        <div className="xl:col-span-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {PROFILES.map((prof, i) => (
              <PC key={prof.name} profile={prof} isActive={activeProfile === i} onClick={() => loadProfile(i)} />
            ))}
          </div>

          <div className={`rounded-xl border p-3 ${sc.bg} ${sc.border}`}>
            <div className="flex items-center gap-2">
              <StatusIcon className={`w-4 h-4 ${sc.text}`} />
              <span className={`text-xs font-bold uppercase ${sc.text}`}>{size.status}</span>
            </div>
            <p className={`text-[10px] mt-1 leading-relaxed ${sc.text} opacity-80`}>{size.statusDetail}</p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <AB level={size.overfillRisk} title="Overfill" message={size.overfillRisk === 'high' ? 'Storage <0.5 days. Risk of tank overflow.' : 'Storage capacity adequate.'} />
            <AB level={size.anaerobicRisk} title="Anaerobic" message={size.anaerobicRisk === 'high' ? 'Detention >24 h. Risk of septicity and odour.' : 'Detention within safe range.'} />
            <AB level={size.polymerRisk} title="Polymer" message={size.polymerRisk === 'high' ? 'Polymer consumption >50 kg/day. High operating cost.' : 'Polymer dose within normal range.'} />
          </div>

          <div className="flex space-x-1 p-1 rounded-xl bg-slate-800">
            <button onClick={() => setActiveTab('thickening')} className={`flex-1 text-xs font-medium py-1.5 rounded-lg transition-all ${activeTab === 'thickening' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-slate-300'}`}>Thickening</button>
            <button onClick={() => setActiveTab('polymer')} className={`flex-1 text-xs font-medium py-1.5 rounded-lg transition-all ${activeTab === 'polymer' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-slate-300'}`}>Polymer</button>
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
              {activeTab === 'thickening' && (
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Thickening Parameters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <S label="Flow Rate" value={params.flowRate} min={5} max={200} step={1} unit="m³/d" icon={Droplets} color="#38bdf8" onChange={(v) => update('flowRate', v)} />
                    <S label="Inlet DS" value={params.inletDS} min={0.5} max={8} step={0.1} unit="%" icon={Trash2} color="#8B4513" onChange={(v) => update('inletDS', v)} />
                    <S label="Target DS" value={params.targetDS} min={1} max={15} step={0.1} unit="%" icon={CheckCircle2} color="#10b981" onChange={(v) => update('targetDS', v)} />
                    <S label="Tank Diameter" value={params.tankDiameter} min={2} max={12} step={0.1} unit="m" icon={Gauge} color="#10b981" onChange={(v) => update('tankDiameter', v)} />
                    <S label="Tank Depth" value={params.tankDepth} min={2} max={8} step={0.1} unit="m" icon={Gauge} color="#10b981" onChange={(v) => update('tankDepth', v)} />
                    <S label="Freeboard" value={params.freeboard} min={0.2} max={1.5} step={0.1} unit="m" icon={ArrowDown} color="#94a3b8" onChange={(v) => update('freeboard', v)} />
                    <S label="Working Volume" value={params.workingVolumePercent} min={30} max={90} step={5} unit="%" icon={Layers} color="#f59e0b" onChange={(v) => update('workingVolumePercent', v)} />
                    <S label="Thickening Time" value={params.thickeningTime} min={4} max={72} step={1} unit="h" icon={Timer} color="#8b5cf6" onChange={(v) => update('thickeningTime', v)} />
                    <S label="Temperature" value={params.temperature} min={5} max={40} step={1} unit="°C" icon={Thermometer} color="#06b6d4" onChange={(v) => update('temperature', v)} />
                    <S label="O&G" value={params.og} min={20} max={1000} step={10} unit="mg/L" icon={Droplets} color="#f59e0b" onChange={(v) => update('og', v)} />
                    <S label="Influent pH" value={params.ph} min={4} max={9.5} step={0.1} unit="" icon={FlaskConical} color="#a855f7" onChange={(v) => update('ph', v)} />
                    <S label="Alkalinity" value={params.alkalinity} min={10} max={500} step={5} unit="mg/L CaCO₃" icon={Beaker} color="#10b981" onChange={(v) => update('alkalinity', v)} />
                    <S label="Turbidity" value={params.turbidity} min={10} max={2000} step={10} unit="NTU" icon={Filter} color="#f59e0b" onChange={(v) => update('turbidity', v)} />
                    <S label="Conductivity" value={params.conductivity} min={100} max={10000} step={50} unit="μS/cm" icon={Zap} color="#fbbf24" onChange={(v) => update('conductivity', v)} />
                    <S label="Hardness" value={params.hardness} min={10} max={1000} step={10} unit="mg/L CaCO₃" icon={Droplets} color="#3b82f6" onChange={(v) => update('hardness', v)} />
                    <S label="NH₃-N" value={params.nh3n} min={1} max={200} step={1} unit="mg/L" icon={Beaker} color="#8b5cf6" onChange={(v) => update('nh3n', v)} />
                    <S label="Total Phosphorus" value={params.tp} min={0.5} max={50} step={0.5} unit="mg/L" icon={FlaskConical} color="#ec4899" onChange={(v) => update('tp', v)} />
                  </div>
                </div>
              )}
              {activeTab === 'polymer' && (
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Polymer Dosing</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <S label="Polymer Dose" value={params.polymerDose} min={0} max={15} step={0.1} unit="kg/t DS" icon={Zap} color="#f59e0b" onChange={(v) => update('polymerDose', v)} />
                    <S label="Polymer Concentration" value={params.polymerConcentration} min={0.1} max={1} step={0.05} unit="%" icon={FlaskConical} color="#f59e0b" onChange={(v) => update('polymerConcentration', v)} />
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Performance Gauges */}
          <div className="p-4 space-y-3 rb-inner-card">
            <h3 className="text-xs font-semibold text-slate-200 flex items-center gap-2"><Gauge className="w-3.5 h-3.5 text-sky-400" /> Performance Gauges</h3>
            <div className="grid grid-cols-2 gap-3">
              <G value={size.thickeningRate} max={200} label="Thickening" unit="%" color="#f59e0b" />
              <G value={size.volumeReduction} max={80} label="Volume Reduction" unit="%" color="#10b981" />
              <G value={size.actualFlux} max={50} label="Solids Flux" unit="kg/m²·h" color="#06b6d4" />
              <G value={size.storageDays} max={7} label="Storage" unit="days" color="#8b5cf6" />
            </div>
          </div>

          {/* Sizing Panel */}
          <div className="p-4 space-y-2 rb-inner-card">
            <h3 className="text-xs font-semibold text-slate-200 flex items-center gap-2"><BarChart3 className="w-3.5 h-3.5 text-emerald-400" /> Sizing & Performance</h3>
            <div className="space-y-1.5 text-[10px]">
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Tank Volume</span><span className="text-rb-brand font-bold">{fmt(size.tankVolume, 1)} m³</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Working Volume</span><span className="text-rb-brand font-bold">{fmt(size.workingVolume, 1)} m³</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Tank Area</span><span className="text-rb-brand font-bold">{fmt(size.tankArea, 1)} m²</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Inlet DS</span><span className="text-rb-brand font-bold">{fmt(size.inletTonneDS, 2)} t/d</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Thickened Volume</span><span className="text-rb-brand font-bold">{fmt(size.thickenedVolume, 1)} m³/d</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Supernatant Volume</span><span className="text-rb-brand font-bold">{fmt(size.supernatantVolume, 1)} m³/d</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Thickening Factor</span><span className="text-rb-brand font-bold">{fmt(size.thickeningFactor, 1)}×</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Storage Days</span><span className="text-rb-brand font-bold">{fmt(size.storageDays, 1)} d</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Detention Time</span><span className="text-rb-brand font-bold">{fmt(size.detentionTime, 1)} h</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Peak Detention</span><span className="text-rb-brand font-bold">{fmt(size.peakDetention, 1)} h</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Polymer Daily</span><span className="text-rb-brand font-bold">{fmt(size.polymerDaily, 1)} kg/d</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Polymer Volume</span><span className="text-rb-brand font-bold">{fmt(size.polymerVolume, 1)} L/d</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Polymer Pump</span><span className="text-rb-brand font-bold">{fmt(size.polymerPumpRate, 1)} mL/min</span></div>
            </div>
          </div>

          {/* Design Notes */}
          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-400" />Design Notes &amp; Warnings</h3>
            <div className="space-y-2 text-xs">
              {size.actualFlux > 30 && <div className="p-2 rounded-lg bg-rose-500/5 border border-rose-500/20 text-rose-400">⚠️ Solids flux {fmt(size.actualFlux, 1)} kg/m²·h exceeds 30. Risk of solids overflow. Increase tank diameter or polymer dose.</div>}
              {size.storageDays < 0.5 && <div className="p-2 rounded-lg bg-rose-500/5 border border-rose-500/20 text-rose-400">⚠️ Storage {fmt(size.storageDays, 1)} days is very short. Tank may overflow at peak flows.</div>}
              {size.anaerobicRisk === 'high' && <div className="p-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-amber-400">ℹ️ Detention &gt;24 h risks anaerobic conditions. Consider decanting or increasing withdrawal rate.</div>}
              {size.thickeningRate < 30 && <div className="p-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-amber-400">ℹ️ Thickening {fmt(size.thickeningRate, 0)}% is low. Verify target DS is realistic for gravity thickening.</div>}
              <div className="p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-emerald-400">ℹ️ Vesilind: v = v₀·exp(-k·C). Polymer boost = {fmt((size as any).enhancedSettlingVelocity / Math.max((size as any).settlingVelocity, 0.001), 2)}×. Detention τ = V/Q.</div>
            </div>
          </div>

          {/* Science Validation Accordion */}
          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Microscope className="w-4 h-4 text-sky-400" />Science Validation</h3>
            <ScienceAccordion sections={scienceSections} />
          </div>
        </div>
      </div>
    </div>
  );
}
